import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  MapPin,
  MessageSquare,
  Send,
  Phone,
  Linkedin,
  Building2,
} from "lucide-react";

import {
  TechLabel,
  StatusBadge,
  CornerMarkers,
  PageContainer,
  PrimaryButton,
  SkeuInput,
  SkeuTextarea,
  motionFadeUp,
  C,
} from "../../design-system";

const offices = [
  { code: "IN-HQ",  city: "Hyderabad",     country: "India",  detail: "Headquarters · primary engineering" },
  { code: "IN-CHN", city: "Chennai",       country: "India",  detail: "South India operations" },
  { code: "UK-BFD", city: "Bradford",      country: "UK",     detail: "Europe & UK accounts" },
  { code: "US-NJ",  city: "Jersey City",   country: "USA",    detail: "East-coast partnerships" },
  { code: "US-MT",  city: "Kalispell, MT", country: "USA",    detail: "West & Central support" },
];

const subjects = [
  "Book a demo",
  "Sales · pricing",
  "Partnership",
  "Technical support",
  "Press / media",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    subject: subjects[0],
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3500);
    setForm({ name: "", email: "", company: "", subject: subjects[0], message: "" });
  };

  return (
    <div
      className="relative"
      style={{
        background: C.paper,
        color: C.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* HERO */}
      <section
        className="relative pt-16 pb-16 md:pt-24 md:pb-20 border-b ds-bg-spotlight"
        style={{ borderColor: C.hairline }}
      >
        <PageContainer>
          <motion.div initial="hidden" animate="show" variants={motionFadeUp}>
            <motion.div variants={motionFadeUp} custom={0}>
              <StatusBadge>Contact · channels open</StatusBadge>
            </motion.div>
            <motion.h1
              variants={motionFadeUp}
              custom={1}
              className="mt-5 text-5xl sm:text-6xl lg:text-[5rem] xl:text-[5.75rem] font-extrabold leading-[0.92] tracking-tight max-w-4xl"
              style={{ color: C.forest }}
            >
              Let's talk hiring.
              <br />
              <span style={{ color: `${C.forest}80` }}>Not pitches.</span>
            </motion.h1>
            <motion.p
              variants={motionFadeUp}
              custom={2}
              className="mt-6 text-lg md:text-xl leading-relaxed max-w-2xl"
              style={{ color: C.grid }}
            >
              Tell us what your team is using today and where it hurts. We'll come
              back with a plan and a real demo, not a sales spiel.
            </motion.p>
          </motion.div>
        </PageContainer>
      </section>

      {/* FORM + DIRECT CHANNELS */}
      <section className="py-16 md:py-24 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
            {/* FORM */}
            <div className="lg:col-span-7">
              <div
                className="relative border p-7 md:p-10"
                style={{ borderColor: C.hairline, background: C.paper }}
              >
                <CornerMarkers color={C.forest} />

                <div className="flex items-center justify-between mb-7 flex-wrap gap-3">
                  <StatusBadge>Send us a note</StatusBadge>
                  <TechLabel style={{ color: `${C.grid}99` }}>
                    response · within 1 business day
                  </TechLabel>
                </div>

                <form onSubmit={onSubmit} className="space-y-5">
                  <Field
                    id="contact-name"
                    label="Full name"
                    required
                    value={form.name}
                    onChange={onChange("name")}
                    placeholder="Ada Lovelace"
                  />

                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field
                      id="contact-email"
                      label="Work email"
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange("email")}
                      placeholder="ada@company.com"
                    />
                    <Field
                      id="contact-company"
                      label="Company"
                      value={form.company}
                      onChange={onChange("company")}
                      placeholder="Acme Inc."
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-subject" className="block mb-2">
                      <TechLabel style={{ color: C.grid }}>Subject</TechLabel>
                    </label>
                    <select
                      id="contact-subject"
                      value={form.subject}
                      onChange={onChange("subject")}
                      className="ds-skeu-input w-full h-11 px-3 text-sm focus:outline-none"
                      style={{ color: C.forest, fontFamily: "inherit" }}
                    >
                      {subjects.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block mb-2">
                      <TechLabel style={{ color: C.grid }}>Message</TechLabel>
                    </label>
                    <SkeuTextarea
                      id="contact-message"
                      rows={5}
                      required
                      value={form.message}
                      onChange={onChange("message")}
                      placeholder="What's your hiring stack today, and what's slowing you down?"
                    />
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                    <TechLabel style={{ color: `${C.grid}99` }}>
                      no spam · we read every reply
                    </TechLabel>
                    <PrimaryButton type="submit" className="w-full sm:w-auto">
                      <Send className="w-4 h-4 mr-2" />
                      Send message
                    </PrimaryButton>
                  </div>

                  {submitted && (
                    <motion.div
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-3 p-3 border text-sm"
                      style={{
                        borderColor: `${C.mint}66`,
                        background: `${C.mint}22`,
                        color: C.forest,
                      }}
                    >
                      Got it, we'll reply within one business day.
                    </motion.div>
                  )}
                </form>
              </div>
            </div>

            {/* DIRECT CHANNELS */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <StatusBadge>Direct channels</StatusBadge>
                <h2
                  className="mt-4 text-3xl md:text-4xl font-bold leading-[1.05] tracking-tight"
                  style={{ color: C.forest }}
                >
                  Or reach out the
                  <br />
                  <span style={{ color: `${C.forest}80` }}>fast way.</span>
                </h2>
              </div>

              <Channel
                icon={Mail}
                title="Email"
                primary="Info@webidealglobal.com"
                primaryHref="mailto:Info@webidealglobal.com"
                secondary="sales@webidealglobal.com"
                secondaryHref="mailto:sales@webidealglobal.com"
              />

              <Channel
                icon={Phone}
                title="WhatsApp · India"
                primary="+91 95810 62681"
                primaryHref="https://wa.me/919581062681"
              />

              <Channel
                icon={Phone}
                title="WhatsApp · UK"
                primary="+44 7359 973201"
                primaryHref="https://wa.me/447359973201"
              />

              <Channel
                icon={Linkedin}
                title="LinkedIn"
                primary="WebIdeal Software Solutions"
                primaryHref="https://lnkd.in/dwRFf3Bi"
              />

              <Channel
                icon={MessageSquare}
                title="Live chat"
                primary="Bottom-right of any page"
                disabled
              />
            </div>
          </div>
        </PageContainer>
      </section>

      {/* OFFICES */}
      <section className="py-20 md:py-24" style={{ background: C.forest, color: C.paper }}>
        <PageContainer>
          <div className="mb-12 max-w-2xl">
            <StatusBadge color={C.mint}>Where we are</StatusBadge>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight">
              Five offices.
              <br />
              <span style={{ color: C.mint }}>Three regions.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-px" style={{ background: "rgba(158,255,191,0.18)" }}>
            {offices.map((o) => (
              <div key={o.code} className="p-6 md:p-7 flex flex-col gap-2.5" style={{ background: C.forest }}>
                <div className="flex items-center justify-between">
                  <span
                    className="inline-flex items-center justify-center px-2 py-1 text-[10px] font-bold border"
                    style={{
                      letterSpacing: "0.16em",
                      borderColor: `${C.mint}66`,
                      color: C.mint,
                    }}
                  >
                    {o.code}
                  </span>
                  <Building2 className="w-4 h-4" style={{ color: "rgba(158,255,191,0.5)" }} />
                </div>
                <h3 className="text-lg font-bold leading-tight">{o.city}</h3>
                <TechLabel style={{ color: "rgba(158,255,191,0.7)" }}>{o.country}</TechLabel>
                <p className="text-xs leading-relaxed" style={{ color: "rgba(247,247,245,0.6)" }}>
                  {o.detail}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-2">
            <MapPin className="w-4 h-4" style={{ color: C.mint }} />
            <TechLabel style={{ color: "rgba(247,247,245,0.6)" }}>
              global presence · status · all online
            </TechLabel>
          </div>
        </PageContainer>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  HELPERS                                                            */
/* ------------------------------------------------------------------ */
function Field({ id, label, type = "text", required, value, onChange, placeholder }) {
  return (
    <div>
      <label htmlFor={id} className="block mb-2">
        <TechLabel style={{ color: C.grid }}>{label}</TechLabel>
      </label>
      <SkeuInput
        id={id}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
    </div>
  );
}

function Channel({ icon: Icon, title, primary, primaryHref, secondary, secondaryHref, disabled }) {
  const Wrapper = ({ children, href }) =>
    disabled || !href ? (
      <span className="block">{children}</span>
    ) : (
      <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="block">
        {children}
      </a>
    );

  return (
    <div
      className="border p-5 md:p-6 flex items-start gap-4 transition-colors duration-200 hover:bg-white"
      style={{ borderColor: C.hairline, background: C.paper }}
    >
      <div
        className="w-10 h-10 flex-shrink-0 flex items-center justify-center"
        style={{ background: C.forest, color: C.paper }}
      >
        <Icon className="w-4 h-4" strokeWidth={1.6} />
      </div>
      <div className="min-w-0 flex-1">
        <TechLabel style={{ color: `${C.grid}99` }}>{title}</TechLabel>
        <Wrapper href={primaryHref}>
          <p
            className="mt-1 text-sm font-semibold truncate transition-colors hover:text-[#1A3C2B]"
            style={{ color: C.forest }}
          >
            {primary}
          </p>
        </Wrapper>
        {secondary && (
          <Wrapper href={secondaryHref}>
            <p
              className="mt-0.5 text-sm truncate transition-colors hover:text-[#1A3C2B]"
              style={{ color: C.grid }}
            >
              {secondary}
            </p>
          </Wrapper>
        )}
      </div>
    </div>
  );
}
